import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { 
  ArrowLeft, Globe, Building2, RefreshCw, ArrowRight, ExternalLink,
  Smartphone, Zap, Shield, Code, Search, Mail, Copy, Check,
  AlertTriangle, CheckCircle, XCircle, Clock
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { AdminLayout } from "./AdminLayout";
import { API } from "../../App";

const INDUSTRIES = {
  hospitality: "Hospitality",
  trades: "Trades",
  health_wellness: "Health & Wellness",
  professional_services: "Professional Services",
  retail: "Retail",
  other: "Other"
};

const PRIORITY_COLORS = {
  high: "bg-red-500/20 text-red-400 border-red-500/30",
  medium: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
  low: "bg-gray-500/20 text-gray-400 border-gray-500/30"
};

const ScoreIndicator = ({ label, score, icon: Icon }) => {
  const getColor = (score) => {
    if (score >= 70) return "text-red-400"; // High need = red (they need help)
    if (score >= 40) return "text-yellow-400";
    return "text-green-400"; // Low need = green (site is fine)
  };

  return (
    <div className="bg-[#0E0E0E] p-4 border border-[#2E2E2E]">
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4 text-[#5F8FB8]" />
        <span className="text-xs text-[#8A8A8A] uppercase">{label}</span>
      </div>
      <div className={`text-3xl font-bold ${getColor(score)}`}>{score}</div>
      <div className="text-xs text-[#8A8A8A]">/ 100</div>
    </div>
  );
};

const IssuesList = ({ title, issues, icon: Icon }) => {
  if (!issues || issues.length === 0) return null;
  
  return (
    <div className="space-y-2">
      <h4 className="text-sm font-medium flex items-center gap-2">
        <Icon className="w-4 h-4 text-[#5F8FB8]" />
        {title}
      </h4>
      <ul className="space-y-1">
        {issues.map((issue, i) => (
          <li key={i} className="text-sm text-[#8A8A8A] flex items-start gap-2">
            <XCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
            {issue}
          </li>
        ))}
      </ul>
    </div>
  );
};

const LeadEngineDetail = () => {
  const { prospectId } = useParams();
  const navigate = useNavigate();
  const [prospect, setProspect] = useState(null);
  const [scan, setScan] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [copiedTemplate, setCopiedTemplate] = useState(null);

  useEffect(() => {
    fetchProspect();
    fetchTemplates();
  }, [prospectId]);

  const fetchProspect = async () => {
    try {
      const response = await axios.get(`${API}/lead-engine/prospects/${prospectId}`);
      setProspect(response.data.prospect);
      setScan(response.data.scan);
    } catch (error) {
      toast.error("Failed to load prospect");
      navigate("/admin/lead-engine");
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/lead-engine/templates`);
      setTemplates(response.data);
    } catch (error) {
      console.error("Failed to load templates");
    }
  };

  const handleScan = async () => {
    setScanning(true);
    try {
      await axios.post(`${API}/lead-engine/prospects/${prospectId}/scan`);
      toast.success("Scan started");
      // Poll for completion
      const checkScan = setInterval(async () => {
        const response = await axios.get(`${API}/lead-engine/prospects/${prospectId}`);
        if (response.data.prospect.status === "scanned") {
          clearInterval(checkScan);
          setProspect(response.data.prospect);
          setScan(response.data.scan);
          setScanning(false);
          toast.success("Scan complete");
        }
      }, 2000);
      // Timeout after 30s
      setTimeout(() => {
        clearInterval(checkScan);
        setScanning(false);
        fetchProspect();
      }, 30000);
    } catch (error) {
      toast.error("Failed to start scan");
      setScanning(false);
    }
  };

  const handlePushToPipeline = async () => {
    try {
      const response = await axios.post(`${API}/lead-engine/prospects/${prospectId}/push-to-pipeline`);
      toast.success("Pushed to Pipeline");
      fetchProspect();
    } catch (error) {
      toast.error("Failed to push to Pipeline");
    }
  };

  const copyTemplate = (template) => {
    // Replace placeholders
    let subject = template.subject
      .replace("{{company_name}}", prospect?.company_name || "")
      .replace("{{contact_name}}", prospect?.contact_name || "there");
    
    let body = template.body
      .replace(/{{company_name}}/g, prospect?.company_name || "")
      .replace(/{{contact_name}}/g, prospect?.contact_name || "there")
      .replace(/{{industry}}/g, INDUSTRIES[prospect?.industry] || prospect?.industry || "")
      .replace(/{{load_time}}/g, scan?.page_load_time_ms ? (scan.page_load_time_ms / 1000).toFixed(1) : "N/A")
      .replace(/{{sender_name}}/g, "[Your Name]");
    
    navigator.clipboard.writeText(`Subject: ${subject}\n\n${body}`);
    setCopiedTemplate(template.template_id);
    toast.success("Template copied");
    setTimeout(() => setCopiedTemplate(null), 2000);
  };

  if (loading) {
    return (
      <AdminLayout title="Prospect">
        <div className="p-8 text-center text-[#8A8A8A]">Loading...</div>
      </AdminLayout>
    );
  }

  if (!prospect) return null;

  return (
    <AdminLayout title="Prospect Details">
      <div className="space-y-6">
        {/* Back */}
        <Link to="/admin/lead-engine" className="inline-flex items-center gap-2 text-sm text-[#8A8A8A] hover:text-[#F5F4EF]">
          <ArrowLeft className="w-4 h-4" /> Back to Lead Engine
        </Link>

        {/* Header */}
        <div className="bg-[#1C1C1C] border border-[#2E2E2E] p-6">
          <div className="flex flex-col lg:flex-row justify-between gap-6">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-xl font-bold">{prospect.company_name}</h1>
                <span className={`px-2 py-1 text-xs uppercase border ${PRIORITY_COLORS[prospect.priority]}`}>
                  {prospect.priority} priority
                </span>
              </div>
              <a 
                href={prospect.website_url.startsWith('http') ? prospect.website_url : `https://${prospect.website_url}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#5F8FB8] hover:underline flex items-center gap-1 mb-3"
              >
                {prospect.website_url} <ExternalLink className="w-4 h-4" />
              </a>
              <div className="flex gap-4 text-sm text-[#8A8A8A]">
                <span>{INDUSTRIES[prospect.industry]}</span>
                {prospect.contact_name && <span>{prospect.contact_name}</span>}
                {prospect.contact_email && <span>{prospect.contact_email}</span>}
              </div>
            </div>
            <div className="flex gap-2">
              {prospect.status !== "pushed" && (
                <>
                  <button 
                    onClick={handleScan} 
                    disabled={scanning}
                    className="btn-secondary text-sm inline-flex items-center gap-2"
                  >
                    <RefreshCw className={`w-4 h-4 ${scanning ? 'animate-spin' : ''}`} />
                    {scanning ? "Scanning..." : "Scan Website"}
                  </button>
                  {prospect.status === "scanned" && prospect.priority !== "low" && (
                    <button 
                      onClick={handlePushToPipeline}
                      className="btn-primary text-sm inline-flex items-center gap-2"
                    >
                      <ArrowRight className="w-4 h-4" /> Push to Pipeline
                    </button>
                  )}
                </>
              )}
              {prospect.status === "pushed" && (
                <span className="px-4 py-2 bg-green-500/20 text-green-400 text-sm">
                  Pushed to Pipeline
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Scores */}
        <div className="grid grid-cols-3 gap-4">
          <ScoreIndicator label="Intent" score={prospect.intent_score} icon={Clock} />
          <ScoreIndicator label="Need" score={prospect.need_score} icon={AlertTriangle} />
          <ScoreIndicator label="Ability to Pay" score={prospect.ability_score} icon={Building2} />
        </div>

        {/* Reason to Contact */}
        {prospect.reason_to_contact && (
          <div className="bg-[#1C1C1C] border border-[#2E2E2E] p-6">
            <h3 className="text-sm font-medium text-[#8A8A8A] uppercase tracking-wide mb-3">Reason to Contact</h3>
            <p className="text-[#F5F4EF]">{prospect.reason_to_contact}</p>
          </div>
        )}

        {/* Scan Results */}
        {scan && (
          <div className="bg-[#1C1C1C] border border-[#2E2E2E] p-6">
            <h3 className="text-sm font-medium text-[#8A8A8A] uppercase tracking-wide mb-4">Website Analysis</h3>
            
            {/* Score Bars */}
            <div className="grid grid-cols-5 gap-4 mb-6">
              {[
                { label: "Mobile", score: scan.mobile_score, icon: Smartphone },
                { label: "Speed", score: scan.speed_score, icon: Zap },
                { label: "SSL", score: scan.ssl_score, icon: Shield },
                { label: "Tech Stack", score: scan.tech_stack_score, icon: Code },
                { label: "SEO", score: scan.seo_score, icon: Search }
              ].map((item) => (
                <div key={item.label} className="text-center">
                  <item.icon className="w-5 h-5 mx-auto mb-2 text-[#5F8FB8]" />
                  <div className="text-xs text-[#8A8A8A] mb-1">{item.label}</div>
                  <div className="text-lg font-bold">{item.score}</div>
                  <div className="w-full bg-[#2E2E2E] h-1 mt-1 rounded">
                    <div 
                      className={`h-1 rounded ${
                        item.score >= 70 ? 'bg-green-500' : 
                        item.score >= 40 ? 'bg-yellow-500' : 'bg-red-500'
                      }`}
                      style={{ width: `${item.score}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Detected Tech */}
            {(scan.detected_cms || scan.detected_technologies?.length > 0) && (
              <div className="mb-6 p-4 bg-[#0E0E0E] border border-[#2E2E2E]">
                <h4 className="text-sm font-medium mb-2">Detected Technology</h4>
                <div className="flex flex-wrap gap-2">
                  {scan.detected_cms && (
                    <span className="px-2 py-1 bg-[#5F8FB8]/20 text-[#5F8FB8] text-xs">
                      {scan.detected_cms}
                    </span>
                  )}
                  {scan.detected_technologies?.map((tech, i) => (
                    <span key={i} className="px-2 py-1 bg-[#2E2E2E] text-[#8A8A8A] text-xs">
                      {tech}
                    </span>
                  ))}
                </div>
                {scan.page_load_time_ms && (
                  <p className="text-xs text-[#8A8A8A] mt-2">
                    Load time: {(scan.page_load_time_ms / 1000).toFixed(2)}s
                  </p>
                )}
              </div>
            )}

            {/* Issues */}
            <div className="grid grid-cols-2 gap-6">
              <IssuesList title="Mobile Issues" issues={scan.mobile_issues} icon={Smartphone} />
              <IssuesList title="Speed Issues" issues={scan.speed_issues} icon={Zap} />
              <IssuesList title="SSL Issues" issues={scan.ssl_issues} icon={Shield} />
              <IssuesList title="Tech Stack Issues" issues={scan.tech_stack_issues} icon={Code} />
              <IssuesList title="SEO Issues" issues={scan.seo_issues} icon={Search} />
            </div>
          </div>
        )}

        {/* Outreach Templates */}
        <div className="bg-[#1C1C1C] border border-[#2E2E2E] p-6">
          <h3 className="text-sm font-medium text-[#8A8A8A] uppercase tracking-wide mb-4">Outreach Templates</h3>
          <div className="grid gap-4">
            {templates.map((template) => (
              <div key={template.template_id} className="p-4 bg-[#0E0E0E] border border-[#2E2E2E]">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h4 className="font-medium">{template.name}</h4>
                    <p className="text-xs text-[#8A8A8A]">{template.subject}</p>
                  </div>
                  <button
                    onClick={() => copyTemplate(template)}
                    className="btn-secondary text-xs inline-flex items-center gap-1"
                  >
                    {copiedTemplate === template.template_id ? (
                      <><Check className="w-3 h-3" /> Copied</>
                    ) : (
                      <><Copy className="w-3 h-3" /> Copy</>
                    )}
                  </button>
                </div>
                <p className="text-sm text-[#8A8A8A] whitespace-pre-line line-clamp-3">
                  {template.body.substring(0, 200)}...
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default LeadEngineDetail;
